import { formatFileSize, formatDate } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';
import FileThumbnail from './FileThumbnail';

interface FileListProps {
  files: DriveItem[];
  parentId: string | null;
  selectedItemId: string | null;
  selectedIds: Set<string>;
  dragOverFolderId: string | null;
  isTrashView?: boolean;
  onFileClick: (file: DriveItem, e?: React.MouseEvent) => void;
  onFolderClick: (folderId: string) => void;
  onContextMenu: (e: React.MouseEvent, item: DriveItem) => void;
  onDragStart: (e: React.DragEvent, item: DriveItem) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, folderId: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, folderId: string) => void;
  onToggleSelect: (itemId: string, e: React.MouseEvent) => void;
}

function getDaysLeft(deletedAt?: string): string {
  if (!deletedAt) return '';
  const TRASH_RETENTION_DAYS = 30;
  const now = new Date();
  const deleted = new Date(deletedAt);
  const diffMs = now.getTime() - deleted.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const remaining = TRASH_RETENTION_DAYS - diffDays;
  if (remaining <= 0) return '곧 삭제됨';
  if (remaining === 1) return '내일 삭제됨';
  return `${remaining}일 남음`;
}

export default function FileList({
  files,
  parentId,
  selectedItemId,
  selectedIds,
  dragOverFolderId,
  isTrashView = false,
  onFileClick,
  onFolderClick,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onToggleSelect,
}: FileListProps) {
  const items = isTrashView ? files : files.filter((f) => f.parentId === parentId);
  const folders = items.filter((f) => f.type === 'folder');
  const fileItems = items.filter((f) => f.type === 'file');
  const sorted = [...folders, ...fileItems];

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-foreground-500 animate-in fade-in duration-300">
        <span className="w-20 h-20 flex items-center justify-center mb-4">
          <i className={`text-5xl ${isTrashView ? 'ri-delete-bin-line text-foreground-300' : 'ri-folder-open-line text-secondary-300'}`}></i>
        </span>
        <p className="text-lg font-medium text-foreground-600">
          {isTrashView ? '휴지통이 비어 있습니다' : '이 폴더는 비어 있습니다'}
        </p>
        <p className="text-sm text-foreground-500 mt-1">
          {isTrashView ? '삭제된 파일이 여기에 30일간 보관됩니다' : '파일을 업로드하거나 새 폴더를 만들어보세요'}
        </p>
      </div>
    );
  }

  return (
    <div className="border border-background-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider border-b border-background-200">
        <div className="col-span-1"></div>
        <div className="col-span-4">이름</div>
        <div className="col-span-2">크기</div>
        <div className="col-span-2">유형</div>
        <div className={`${isTrashView ? 'col-span-2' : 'col-span-3'}`}>
          {isTrashView ? '남은 기간' : '수정일'}
        </div>
        {isTrashView && <div className="col-span-1">삭제일</div>}
      </div>
      {sorted.map((item, index) => {
        const isSelected = selectedIds.has(item.id);
        const isContextActive = selectedItemId === item.id;
        const isDragOver = item.type === 'folder' && dragOverFolderId === item.id && !isTrashView;

        const rowBg = isSelected
          ? 'bg-primary-50/70'
          : isContextActive
            ? 'bg-primary-50/50'
            : isDragOver
              ? 'bg-accent-50/80 ring-2 ring-accent-300 ring-inset'
              : isTrashView
                ? 'hover:bg-accent-50/40'
                : 'hover:bg-background-100';

        return (
          <div
            key={item.id}
            className={`w-full grid grid-cols-12 gap-2 px-4 py-2.5 transition-all duration-150 border-b border-background-200 last:border-b-0 items-center group animate-in fade-in slide-in-from-bottom-2 duration-200 ${rowBg}`}
            style={{ animationDelay: `${Math.min(index * 25, 300)}ms`, animationFillMode: 'backwards' }}
            onContextMenu={(e) => onContextMenu(e, item)}
            draggable={!isTrashView}
            onDragStart={(e) => onDragStart(e, item)}
            onDragEnd={onDragEnd}
            {...(item.type === 'folder' && !isTrashView
              ? {
                  onDragOver: (e: React.DragEvent) => onDragOver(e, item.id),
                  onDragLeave,
                  onDrop: (e: React.DragEvent) => onDrop(e, item.id),
                }
              : {})}
          >
            <div className="col-span-1 flex items-center justify-center">
              <button
                onClick={(e) => onToggleSelect(item.id, e)}
                className={`w-5 h-5 flex items-center justify-center rounded border transition-all duration-150 cursor-pointer ${
                  isSelected
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-background-300 bg-background-50 text-transparent hover:border-foreground-400 hover:text-foreground-400 opacity-0 group-hover:opacity-100'
                }`}
                title={isSelected ? '선택 해제' : '선택'}
              >
                <i className={`${isSelected ? 'ri-check-line' : 'ri-checkbox-blank-circle-line'} text-xs`}></i>
              </button>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (item.type === 'folder') {
                  onFolderClick(item.id);
                } else {
                  onFileClick(item, e);
                }
              }}
              className="col-span-4 flex items-center gap-3 min-w-0 text-left cursor-pointer"
            >
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-transform duration-200 ${
                isDragOver ? 'scale-110' : ''
              }`}>
                {item.type === 'folder' ? (
                  <div className="relative w-full h-full flex items-center justify-center rounded-lg bg-accent-50">
                    <i className={`ri-folder-3-fill text-lg ${isTrashView ? 'text-accent-400' : 'text-accent-500'}`}></i>
                    {isDragOver && (
                      <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-accent-500/20">
                        <i className="ri-folder-transfer-line text-sm text-accent-600"></i>
                      </span>
                    )}
                  </div>
                ) : (
                  <FileThumbnail item={item} size="sm" />
                )}
              </span>
              <span className={`text-sm font-medium truncate ${isTrashView ? 'text-foreground-600' : 'text-foreground-800'}`}>{item.name}</span>
            </button>
            <div className="col-span-2 text-sm text-foreground-600">
              {item.type === 'folder' ? '—' : formatFileSize(item.size ?? 0)}
            </div>
            <div className="col-span-2 text-sm text-foreground-500">
              {item.type === 'folder' ? '폴더' : item.mimeType?.split('/')[1]?.toUpperCase() ?? '파일'}
            </div>
            {isTrashView ? (
              <>
                <div className="col-span-2 text-sm text-accent-500 font-medium">
                  {getDaysLeft(item.deletedAt)}
                </div>
                <div className="col-span-1 text-sm text-foreground-400">
                  {item.deletedAt ? formatDate(item.deletedAt) : '—'}
                </div>
              </>
            ) : (
              <div className="col-span-3 text-sm text-foreground-500">
                {formatDate(item.updatedAt)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}